import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@mlm/ui';

export type StairstepRank = {
  code: string;
  min_ppv: number;
  min_gpv: number;
  min_legs: number;
  /** Min directly-sponsored active slots required for this rank (Phase C). */
  min_directs?: number;
  min_leg_gpv: number | null;
  min_leg_rank: string | null;
  payout_pct: number;
  discount_pct: number;
  /**
   * Same-rank royalty bonus %. When > 0, an equal-rank downline that breaks away
   * pays this rank a royalty on the leg's whole group PV (instead of 0). Set it
   * only on the specific ranks that should earn royalty; blank/0 = off.
   */
  royalty_pct?: number;
  /** Infinity Bonus override % for this rank, read by the infinity_bonus plan (Phase C). */
  percentage_override?: number;
  /** One-time rank-up bonus (paid when the tenant flag stairstep_rankup_bonus is on). */
  reward_cash?: number;
  reward_gc?: number;
};

export type StairstepConfig = {
  ranks: StairstepRank[];
};

export function StairstepEditor({
  value,
  onChange,
  readOnly,
}: {
  value: StairstepConfig;
  onChange: (next: StairstepConfig) => void;
  readOnly?: boolean;
}) {
  function setRank(i: number, patch: Partial<StairstepRank>) {
    const next = value.ranks.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ ranks: next });
  }
  function removeRank(i: number) {
    onChange({ ranks: value.ranks.filter((_, idx) => idx !== i) });
  }
  /** Swap rank i with its neighbour (dir -1 = up = lower rank, +1 = down = higher). */
  function moveRank(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.ranks.length) return;
    const next = value.ranks.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange({ ranks: next });
  }
  function addRank() {
    onChange({
      ranks: [
        ...value.ranks,
        {
          code: `rank_${value.ranks.length + 1}`,
          min_ppv: 0,
          min_gpv: 0,
          min_legs: 0,
          min_directs: 0,
          min_leg_gpv: null,
          min_leg_rank: null,
          payout_pct: 0,
          discount_pct: 0,
          royalty_pct: 0,
          percentage_override: 0,
          reward_cash: 0,
          reward_gc: 0,
        },
      ],
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Ranks</h3>
          <p className="text-[10px] text-muted-foreground">
            Hierarchy = list order, lowest → highest:{' '}
            <strong>top row is the lowest rank, bottom row the highest</strong>{' '}
            (use the ↑/↓ arrows to reorder). A slot earns the highest rank whose
            PPV / GPV / leg thresholds it meets. Hover a column header for what
            it does.
          </p>
        </div>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={addRank}>
            <Plus className="h-3 w-3 mr-1" />
            Add rank
          </Button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border rounded-md min-w-[900px]">
          <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
            <tr>
              <th className="px-3 py-2 font-medium" title="Rank order, lowest → highest. Use the arrows to reorder.">Rank #</th>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium" title="Minimum personal point value (own orders) for this rank.">Min PPV</th>
              <th className="px-3 py-2 font-medium" title="Minimum group point value (own + all descendants) for this rank.">Min GPV</th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum number of qualifying direct legs required for this rank."
              >
                Min legs
              </th>
              <th className="px-3 py-2 font-medium" title="Minimum directly-sponsored ACTIVE slots required, in addition to qualifying legs.">Min directs</th>
              <th className="px-3 py-2 font-medium" title="A leg counts toward Min legs if its group PV is at least this (when Min leg rank is blank).">Min leg GPV</th>
              <th
                className="px-3 py-2 font-medium"
                title="A leg counts toward Min legs only if its OWN rank is at least this rank code."
              >
                Min leg rank
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="This rank's differential commission %. An upline earns (uplinePct − downlinePct) on a downline's PPV."
              >
                Payout %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Same-rank royalty %. When set, an equal-rank downline that breaks away still pays this rank a royalty on the leg's whole group PV (instead of 0). Leave 0 to disable."
              >
                Royalty %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Infinity Bonus override % — read by the separate Infinity Bonus plan, NOT by Stairstep itself."
              >
                Infinity %
              </th>
              <th className="px-3 py-2 font-medium" title="One-time cash bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on).">Reward cash</th>
              <th className="px-3 py-2 font-medium" title="One-time gift-cert bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on).">Reward GC</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {value.ranks.length === 0 ? (
              <tr>
                <td
                  colSpan={14}
                  className="px-3 py-4 text-xs text-muted-foreground text-center"
                >
                  No ranks configured. Click "Add rank" to start.
                </td>
              </tr>
            ) : (
              value.ranks.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <span className="w-5 text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                      {!readOnly ? (
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => moveRank(i, -1)}
                            disabled={i === 0}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                            aria-label="Move rank up (lower)"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveRank(i, 1)}
                            disabled={i === value.ranks.length - 1}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                            aria-label="Move rank down (higher)"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="text"
                      className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                      value={r.code}
                      disabled={readOnly}
                      onChange={(e) => setRank(i, { code: e.target.value })}
                    />
                  </td>
                  {(
                    ['min_ppv', 'min_gpv', 'min_legs', 'min_directs'] as const
                  ).map((key) => (
                    <td className="px-3 py-1.5" key={key}>
                      <input
                        type="number"
                        className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                        value={r[key] ?? 0}
                        disabled={readOnly}
                        step={key === 'min_legs' || key === 'min_directs' ? 1 : 0.01}
                        min={0}
                        onChange={(e) =>
                          setRank(i, { [key]: Number(e.target.value) || 0 } as Partial<StairstepRank>)
                        }
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                      placeholder=""
                      value={r.min_leg_gpv ?? ''}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      onChange={(e) =>
                        setRank(i, {
                          min_leg_gpv:
                            e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    {/* Must be an actual rank CODE — a free-text typo (e.g. a
                        rank number) silently disables the leg-rank gate, so
                        constrain to configured codes. */}
                    <select
                      className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                      value={r.min_leg_rank ?? ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        setRank(i, {
                          min_leg_rank: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    >
                      <option value="">none</option>
                      {value.ranks
                        .filter((rk) => rk.code !== r.code)
                        .map((rk) => (
                          <option key={rk.code} value={rk.code}>
                            {rk.code}
                          </option>
                        ))}
                    </select>
                  </td>
                  {(['payout_pct', 'royalty_pct', 'percentage_override'] as const).map((key) => (
                    <td className="px-3 py-1.5" key={key}>
                      <input
                        type="number"
                        className="h-8 w-20 px-2 rounded-md border bg-background text-xs"
                        value={r[key] ?? 0}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        max={100}
                        onChange={(e) =>
                          setRank(i, { [key]: Number(e.target.value) || 0 } as Partial<StairstepRank>)
                        }
                      />
                    </td>
                  ))}
                  {(['reward_cash', 'reward_gc'] as const).map((key) => (
                    <td className="px-3 py-1.5" key={key}>
                      <input
                        type="number"
                        className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                        value={r[key] ?? 0}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setRank(i, { [key]: Number(e.target.value) || 0 } as Partial<StairstepRank>)
                        }
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1.5">
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={() => removeRank(i)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Remove rank ${r.code}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function emptyStairstepConfig(): StairstepConfig {
  return { ranks: [] };
}
